import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import { useMetricPattern } from '../../charts/performance-chart/funcs';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { serverFilterId, timeRangeFilterId } from '../../filters';

const title = t`Server Disk Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metricPatterns: ['disk:usage:'],
        metrics: ['statusDiskUtil'],
    },
};

const metricPatternMapping = [
    {
        pattern: 'disk:usage:',
        trendOptions: {
            formatter: FORMATTER_TYPES.PERCENT,
            units: '%',
            forceDecimals: true,
        },
    },
];

const serverDiskUtilChartConfig = {
    yAxis: [
        {
            title: {
                text: t`Utilization (%)`,
            },
            ceiling: 100,
        },
    ],
    tooltip: BuiltInTooltip,
    showGranularity: true,
    statusMetric: 'statusDiskUtil',
};

function ServerDiskUtilizationPanel() {
    const data = usePanelDataService(dataSource);
    const firstData = data?.data?.[0];
    const chartTrends = useMetricPattern(metricPatternMapping, firstData);
    const chartDef = useMemo(() => {
        return { ...serverDiskUtilChartConfig, chartTrends };
    }, [chartTrends]);

    if (!data) return null;

    return <PerformanceChart chartDef={chartDef} data={firstData} />;
}

ServerDiskUtilizationPanel.propTypes = panelPropTypes;

ServerDiskUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'ServerDiskUtilTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.SERVER,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Disk utilization trend for a selected server`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, serverFilterId],
        optional: [],
    },
})(ServerDiskUtilizationPanel);
